import { ChevronLeftIcon } from '@chakra-ui/icons';
import {
    AspectRatio,
    Box,
    Button,
    Center,
    Divider,
    Flex,
    Grid,
    GridItem,
    Heading,
    Image,
    Link,
    Text,
} from '@chakra-ui/react';
import { FC } from 'react';
import {
    LoaderFunctionArgs,
    Link as ReactRouterLink,
    useLoaderData,
} from 'react-router-dom';
import PlaceholderImage from '../../../images/logo_becx.png';
import { fetchAppointments } from '../../api/repository/appointment';
import { fetchStock } from '../../api/repository/stock';
import {
    PagePath,
    createAppointmentEditRoute,
    createAppointmentNewRoute,
    createReturnStockRoute,
    createStockUpdateRoute,
} from '../../constants/routes';
import { TaskIdentifiers } from '../../constants/stock';
import {
    canCreateAppointment,
    canUpdateAppointment,
    convertAppointmentFromApi,
} from '../../helpers/appointment';
import { DEFAULT_DATE_TIME_FORMAT, formatDate } from '../../helpers/date';
import {
    convertStockItemFromApi,
    findAppointment,
    findStock,
    hasTask,
} from '../../helpers/stock';
import { AppointmentInterface } from '../../types/appointment';
import { StockItemInterface } from '../../types/stock';
import { DownloadableCollection } from '../downloadableCollection/DownloadableCollection';

export const loader = async ({ params }: LoaderFunctionArgs) => {
    const { id: stockNumber } = params;
    const stock = await fetchStock();
    const appointments = await fetchAppointments();

    const formattedAppointments = appointments?.map((item) =>
        convertAppointmentFromApi(item)
    );

    const formattedStock = stock?.map((item) => convertStockItemFromApi(item, formattedAppointments));

    const currentStock = stockNumber
        ? findStock(formattedStock, stockNumber)
        : null;

    if (!currentStock) {
        throw new Response('Not Found', { status: 404 });
    }



    const stockAppointment =
        findAppointment(formattedAppointments, currentStock.stockNumber) ??
        null;

    return {
        currentStock,
        stockAppointment,
    };
};

export const StockDetail: FC = () => {
    const { currentStock, stockAppointment } = useLoaderData() as {
        currentStock: StockItemInterface;
        stockAppointment: AppointmentInterface | null;
    };

    return (
        <Box px={8} py={8}>
            <Link
                as={ReactRouterLink}
                to={PagePath.StockOverview}
                mb={2}
                display={'inline-block'}
            >
                <ChevronLeftIcon />
                Terug naar overzicht
            </Link>
            <Flex
                alignItems={'center'}
                justifyContent={'space-between'}
                flexWrap={'wrap'}
                gap={4}
                mb={6}
            >
                <Heading m={0} color={'secondary'}>
                    Voertuig details
                </Heading>

                {canCreateAppointment(stockAppointment) && (
                    <Button
                        as={ReactRouterLink}
                        to={createAppointmentNewRoute(currentStock.stockNumber)}
                        colorScheme={'primary'}
                    >
                        Afspraak maken
                    </Button>
                )}

                {canUpdateAppointment(stockAppointment) && (
                    <Button
                        as={ReactRouterLink}
                        to={createAppointmentEditRoute(
                            currentStock.stockNumber
                        )}
                        colorScheme={'primary'}
                    >
                        Afspraak aanpassen
                    </Button>
                )}
            </Flex>

            <Grid gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                <GridItem>
                    <Grid
                        gridTemplateColumns={{ base: '1fr', md: 'auto 1fr' }}
                        columnGap={8}
                        rowGap={{ base: 0, md: 2 }}
                    >
                        <GridItem pt={{ base: 2, md: 0 }} fontWeight={'bold'}>
                            Vlootnummer
                        </GridItem>
                        <GridItem>{currentStock.stockNumber}</GridItem>

                        <GridItem pt={{ base: 2, md: 0 }} fontWeight={'bold'}>
                            Kenteken
                        </GridItem>
                        <GridItem>{currentStock.licensePlate}</GridItem>

                        <GridItem pt={{ base: 2, md: 0 }} fontWeight={'bold'}>
                            Type voertuig
                        </GridItem>
                        <GridItem>{currentStock.type.title}</GridItem>

                        <GridItem pt={{ base: 2, md: 0 }} fontWeight={'bold'}>
                            Chassisnummer
                        </GridItem>
                        <GridItem>{currentStock.chassisNumber}</GridItem>

                        <GridItem pt={{ base: 2, md: 0 }} fontWeight={'bold'}>
                            Klant notitie
                        </GridItem>
                        <GridItem>
                            <Flex columnGap={2} flexWrap={'wrap'}>
                                {currentStock.customerNote}
                                <Link
                                    as={ReactRouterLink}
                                    to={createStockUpdateRoute(
                                        currentStock.stockNumber
                                    )}
                                    variant={'primary'}
                                >
                                    Klant notitie aanpassen
                                </Link>
                            </Flex>
                        </GridItem>

                        {hasTask(currentStock.type?.tasks, [
                            TaskIdentifiers.APK,
                        ]) &&
                            currentStock.apkDate && (
                                <>
                                    <GridItem
                                        pt={{ base: 2, md: 0 }}
                                        fontWeight={'bold'}
                                    >
                                        APK datum
                                    </GridItem>
                                    <GridItem>
                                        <Flex columnGap={2} flexWrap={'wrap'}>
                                            {currentStock.isSuspended && (
                                                <Text fontWeight={'bold'}>
                                                    Geschorst
                                                </Text>
                                            )}

                                            {formatDate(currentStock.apkDate)}

                                            {canCreateAppointment(
                                                stockAppointment
                                            ) && (
                                                <Link
                                                    as={ReactRouterLink}
                                                    to={createAppointmentNewRoute(
                                                        currentStock.stockNumber
                                                    )}
                                                    variant={'primary'}
                                                >
                                                    Afspraak maken
                                                </Link>
                                            )}

                                            {canUpdateAppointment(
                                                stockAppointment
                                            ) && (
                                                <Link
                                                    as={ReactRouterLink}
                                                    to={createAppointmentEditRoute(
                                                        currentStock.stockNumber
                                                    )}
                                                    variant={'primary'}
                                                >
                                                    Afspraak aanpassen
                                                </Link>
                                            )}
                                        </Flex>
                                    </GridItem>
                                </>
                            )}

                        {hasTask(currentStock.type?.tasks, [
                            TaskIdentifiers.TACHOGRAPH,
                        ]) &&
                            currentStock.tachographDate && (
                                <>
                                    <GridItem
                                        pt={{ base: 2, md: 0 }}
                                        fontWeight={'bold'}
                                    >
                                        Verloopdatum Tacho ijken
                                    </GridItem>
                                    <GridItem>
                                        <Flex columnGap={2} flexWrap={'wrap'}>
                                            {formatDate(
                                                currentStock.tachographDate
                                            )}

                                            {currentStock.tachographDateIsWithinTwoMonths && (
                                                <Link
                                                    as={ReactRouterLink}
                                                    to={createAppointmentNewRoute(
                                                        currentStock.stockNumber
                                                    )}
                                                    variant={'primary'}
                                                >
                                                    Afspraak maken
                                                </Link>
                                            )}
                                        </Flex>
                                    </GridItem>
                                </>
                            )}

                        {hasTask(currentStock.type?.tasks, [
                            TaskIdentifiers.EXAMINATION,
                        ]) &&
                            currentStock.examinationDate && (
                                <>
                                    <GridItem
                                        pt={{ base: 2, md: 0 }}
                                        fontWeight={'bold'}
                                    >
                                        Verloopdatum Keuring
                                    </GridItem>
                                    <GridItem>
                                        <Flex columnGap={2} flexWrap={'wrap'}>
                                            {formatDate(
                                                currentStock.examinationDate
                                            )}

                                            {currentStock.examinationDateIsWithinTwoMonths && (
                                                <Link
                                                    as={ReactRouterLink}
                                                    to={createAppointmentNewRoute(
                                                        currentStock.stockNumber
                                                    )}
                                                    variant={'primary'}
                                                >
                                                    Afspraak maken
                                                </Link>
                                            )}
                                        </Flex>
                                    </GridItem>
                                </>
                            )}

                        {hasTask(currentStock.type?.tasks, [
                            TaskIdentifiers.TAILGATE,
                        ]) &&
                            currentStock.tailgateDate && (
                                <>
                                    <GridItem
                                        pt={{ base: 2, md: 0 }}
                                        fontWeight={'bold'}
                                    >
                                        Laadklep Keuring
                                    </GridItem>
                                    <GridItem>
                                        <Flex columnGap={2} flexWrap={'wrap'}>
                                            {formatDate(
                                                currentStock.tachographDate
                                            )}

                                            {currentStock.tailgateDateIsWithinTwoMonths && (
                                                <Link
                                                    as={ReactRouterLink}
                                                    to={createAppointmentNewRoute(
                                                        currentStock.stockNumber
                                                    )}
                                                    variant={'primary'}
                                                >
                                                    Afspraak maken
                                                </Link>
                                            )}
                                        </Flex>
                                    </GridItem>
                                </>
                            )}

                        {hasTask(currentStock.type?.tasks, [
                            TaskIdentifiers.CFK_EXAMINATION,
                        ]) &&
                            currentStock.cfkExaminationDate && (
                                <>
                                    <GridItem
                                        pt={{ base: 2, md: 0 }}
                                        fontWeight={'bold'}
                                    >
                                        CFK Keuring
                                    </GridItem>
                                    <GridItem>
                                        <Flex columnGap={2} flexWrap={'wrap'}>
                                            {formatDate(
                                                currentStock.cfkExaminationDate
                                            )}

                                            {currentStock.cfkExaminationDateDateIsWithinTwoMonths && (
                                                <Link
                                                    as={ReactRouterLink}
                                                    to={createAppointmentNewRoute(
                                                        currentStock.stockNumber
                                                    )}
                                                    variant={'primary'}
                                                >
                                                    Afspraak maken
                                                </Link>
                                            )}
                                        </Flex>
                                    </GridItem>
                                </>
                            )}

                        <GridItem pt={{ base: 2, md: 0 }} fontWeight={'bold'}>
                            Type contract
                        </GridItem>
                        <GridItem>
                            <Flex columnGap={2} flexWrap={'wrap'}>
                                {currentStock.contractType}

                                {!currentStock.hasPermanentContract && (
                                    <Link
                                        as={ReactRouterLink}
                                        to={createReturnStockRoute(
                                            currentStock.stockNumber
                                        )}
                                    >
                                        Voertuig inleveren
                                    </Link>
                                )}
                            </Flex>
                        </GridItem>

                        {stockAppointment && (
                            <>
                                <GridItem
                                    pt={{ base: 2, md: 0 }}
                                    fontWeight={'bold'}
                                >
                                    Afspraak voor
                                </GridItem>
                                <GridItem>
                                    <Flex columnGap={2} flexWrap={'wrap'}>
                                        {stockAppointment.tasks
                                            .map((task) => task.task.title)
                                            .join(', ')}
                                    </Flex>
                                </GridItem>

                                <GridItem
                                    pt={{ base: 2, md: 0 }}
                                    fontWeight={'bold'}
                                >
                                    Status afspraak
                                </GridItem>
                                <GridItem>{stockAppointment.state}</GridItem>

                                {stockAppointment.deliveryDate ? (
                                    <>
                                        <GridItem
                                            pt={{ base: 2, md: 0 }}
                                            fontWeight={'bold'}
                                        >
                                            Aanleveren op
                                        </GridItem>
                                        <GridItem>
                                            {formatDate(
                                                stockAppointment.deliveryDate,
                                                DEFAULT_DATE_TIME_FORMAT
                                            )}
                                            <Text textStyle={'italic'}>
                                                {stockAppointment.remarks}
                                            </Text>
                                        </GridItem>
                                    </>
                                ) : (
                                    <>
                                        <GridItem
                                            pt={{ base: 2, md: 0 }}
                                            fontWeight={'bold'}
                                        >
                                            Gewenste aanleverdatum
                                        </GridItem>
                                        <GridItem>
                                            <Text>
                                                {formatDate(
                                                    stockAppointment.desiredDeliveryDate,
                                                    DEFAULT_DATE_TIME_FORMAT
                                                )}
                                            </Text>
                                            <Text as={'em'} color={'gray.500'}>
                                                {stockAppointment.remarks}
                                            </Text>
                                        </GridItem>
                                    </>
                                )}
                            </>
                        )}
                    </Grid>
                </GridItem>
                <GridItem>
                    {currentStock.imageUrl ? (
                        <Image src={currentStock.imageUrl} />
                    ) : (
                        <AspectRatio ratio={16 / 9} bgColor={'gray.100'}>
                            <Center>
                                <Image src={PlaceholderImage} opacity={0.3} />
                            </Center>
                        </AspectRatio>
                    )}
                </GridItem>
            </Grid>

            <Divider my={8} />

            <DownloadableCollection
                downloadableCollection={currentStock.downloadableCollection}
            />
        </Box>
    );
};
